import { useState, useEffect, useCallback } from 'react';
import { store } from 'shared/library/use-local-storage/local-storage.web';

export { store };

export type LocalStorage = {
  getItem: (key: string) => Promise<string | null>;
  setItem: (key: string, value: string) => Promise<void>;
  removeItem: (key: string) => Promise<void>;
};

export function useLocalStorage<T, U = null>(key: string, defaultValue = null as U) {
  const [value, setValue] = useState<T | U>(defaultValue);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const stored = await store.getItem(key);
        if (stored !== null) {
          setValue(JSON.parse(stored));
        }
      } catch (err) {
        console.warn('useLocalStorage load error', err);
      } finally {
        setLoaded(true);
      }
    }

    load();
  }, [key]);

  const setStoredValue = useCallback(
    async (newValue: T) => {
      try {
        setValue(newValue);
        await store.setItem(key, JSON.stringify(newValue));
      } catch (err) {
        console.warn('useLocalStorage save error', err);
      }
    },
    [key],
  );

  const remove = useCallback(async () => {
    try {
      await store.removeItem(key);
      setValue(defaultValue);
    } catch (err) {
      console.warn('useLocalStorage remove error', err);
    }
  }, [key, defaultValue]);

  return { value, setStoredValue, loaded, remove };
}
